/**
 * Turning the four views into one wide grid.
 *
 * This is presentation reshaping and nothing else — §4.3 permits "re-layout of computed cells into
 * a matrix" in the browser, and permits only that. There is no arithmetic in this file: no `+`, no
 * `/`, no rounding, no threshold comparison. Every number and every status arrives from SQL. If a
 * figure is missing from the grid, the fix is a column on a view, not a calculation here.
 *
 * The reshape is also only ever done over a **complete** result. `isComplete` below is the gate,
 * and the page must not build a grid without it: a partial `timeslots` result would silently drop
 * cells, and a partial `hours` result would drop whole columns from the header.
 */

/** One row of the `hours` view — the authority for which hourly columns exist, and their order. */
export interface HourRow {
  timeslot: string;
  timeslot_seq: number;
  timeslot_type_name: string;
  /** 1 once the hour has finished on the wall clock. The board dims the rest. */
  is_elapsed: number | null;
  /** 1 for the hour in progress. Never set on a past or future work day — there isn't one. */
  is_current: number | null;
  hourly_total: number | null;
  planned_hourly_total: number | null;
  planned_cumulative_total: number | null;
  perf_to_plan_hour_pct: number | null;
  hour_status: string | null;
  cumulative_output: number | null;
  cumulative_perf_pct: number | null;
  /** Cumulative output against the plan for the hours elapsed — meaningful only where `is_elapsed`. */
  cumulative_pace_pct: number | null;
}

/** One row of the `timeslots` view — a single cell of the grid. */
export interface CellRow {
  sfts_line: string;
  timeslot_seq: number;
  is_elapsed: number | null;
  output: number | null;
  planned_quantity_per_hour: number | null;
  cell_status: string | null;
}

/** One row of the `lines` view. Indexed loosely: the columns come from server metadata. */
export interface LineRow extends Record<string, unknown> {
  sfts_line: string;
  performance_status?: string | null;
  performance_pct?: number | null;
  pace_status?: string | null;
  pace_pct?: number | null;
}

/**
 * A response envelope is usable for reshaping only when every row of it arrived. `total` is the
 * server's count of matching rows, so `rows.length === total` is the whole-result claim; a
 * truncated result fails it even when the counts happen to agree.
 */
export function isComplete(
  data: { rows: unknown[]; total: number; truncated?: boolean } | undefined,
): boolean {
  return data !== undefined && data.truncated !== true && data.rows.length === data.total;
}

/**
 * Cells keyed by line and column ordinal. A `Map` rather than an object so a line code that looks
 * numeric cannot be reordered by JS object key rules.
 */
export type CellIndex = ReadonlyMap<string, ReadonlyMap<number, CellRow>>;

export function indexCells(cells: readonly CellRow[]): CellIndex {
  const index = new Map<string, Map<number, CellRow>>();
  for (const cell of cells) {
    let row = index.get(cell.sfts_line);
    if (row === undefined) {
      row = new Map<number, CellRow>();
      index.set(cell.sfts_line, row);
    }
    row.set(cell.timeslot_seq, cell);
  }
  return index;
}

/**
 * The hourly columns, in the order the server ranked them. Taken from `hours` and never from the
 * cells: deriving the column set from the cells would let a line that produced nothing shrink the
 * header, which is exactly the "deriving which values exist in a dimension from returned rows"
 * that §4.3 gates.
 */
export function hourColumns(hours: readonly HourRow[]): readonly HourRow[] {
  return [...hours].sort((a, b) => a.timeslot_seq - b.timeslot_seq);
}
